import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils.js';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: ToastItem[];
  addToast: (type: ToastType, message: string, title?: string, duration?: number) => void;
  removeToast: (id: string) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((type: ToastType, message: string, title?: string, duration = 4000) => {
    const id = 'toast_' + Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev.slice(-3), { id, type, title, message, duration }]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  }, [removeToast]);

  const success = useCallback((message: string, title?: string) => addToast('success', message, title), [addToast]);
  const error = useCallback((message: string, title?: string) => addToast('error', message, title, 5000), [addToast]);
  const warning = useCallback((message: string, title?: string) => addToast('warning', message, title, 4500), [addToast]);
  const info = useCallback((message: string, title?: string) => addToast('info', message, title), [addToast]);

  // Window CustomEvent listener for cross-component triggers
  useEffect(() => {
    const handleCustomToast = (e: CustomEvent) => {
      const { type = 'info', message, title, duration } = e.detail || {};
      if (message) {
        addToast(type, message, title, duration);
      }
    };
    window.addEventListener('tsos_toast' as any, handleCustomToast);
    return () => window.removeEventListener('tsos_toast' as any, handleCustomToast);
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, warning, info }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Graceful fallback if invoked outside provider
    return {
      toasts: [],
      addToast: () => {},
      removeToast: () => {},
      success: (msg: string) => window.dispatchEvent(new CustomEvent('tsos_toast', { detail: { type: 'success', message: msg } })),
      error: (msg: string) => window.dispatchEvent(new CustomEvent('tsos_toast', { detail: { type: 'error', message: msg } })),
      warning: (msg: string) => window.dispatchEvent(new CustomEvent('tsos_toast', { detail: { type: 'warning', message: msg } })),
      info: (msg: string) => window.dispatchEvent(new CustomEvent('tsos_toast', { detail: { type: 'info', message: msg } })),
    };
  }
  return ctx;
}

function ToastContainer({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 flex flex-col space-y-2.5 max-w-sm w-full pointer-events-none"
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={() => onDismiss(toast.id)} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  const { type, title, message } = toast;

  const config = {
    success: {
      icon: CheckCircle2,
      border: 'border-emerald-500/30 dark:border-emerald-500/40',
      bg: 'bg-white dark:bg-[#12211F]',
      iconColor: 'text-emerald-500 dark:text-emerald-400',
      accent: 'bg-emerald-500',
    },
    error: {
      icon: AlertCircle,
      border: 'border-red-500/30 dark:border-red-500/40',
      bg: 'bg-white dark:bg-[#12211F]',
      iconColor: 'text-red-500 dark:text-red-400',
      accent: 'bg-red-500',
    },
    warning: {
      icon: AlertTriangle,
      border: 'border-amber-500/30 dark:border-amber-500/40',
      bg: 'bg-white dark:bg-[#12211F]',
      iconColor: 'text-amber-500 dark:text-amber-400',
      accent: 'bg-amber-500',
    },
    info: {
      icon: Info,
      border: 'border-teal-500/30 dark:border-teal-500/40',
      bg: 'bg-white dark:bg-[#12211F]',
      iconColor: 'text-teal-600 dark:text-teal-400',
      accent: 'bg-teal-600',
    },
  }[type];

  const Icon = config.icon;
  const duration = toast.duration ?? 4000;

  return (
    <div
      role="alert"
      className={cn(
        'relative overflow-hidden pointer-events-auto flex items-start gap-3 p-4 rounded-xl shadow-xl border transition-all duration-normal animate-in fade-in slide-in-from-bottom-2',
        config.bg,
        config.border
      )}
    >
      <div className="shrink-0 mt-0.5">
        <Icon className={cn('h-5 w-5', config.iconColor)} />
      </div>
      <div className="flex-1 min-w-0 pr-1 pb-1">
        {title && <p className="text-xs font-bold text-brand-ink leading-tight">{title}</p>}
        <p className={cn('text-xs leading-relaxed text-brand-muted font-medium', title && 'mt-0.5')}>
          {message}
        </p>
      </div>
      <button
        onClick={onDismiss}
        className="shrink-0 p-1 rounded-md text-brand-muted hover:text-brand-ink hover:bg-brand-tint/30 transition-colors"
        title="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>

      {duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/5 dark:bg-white/10 overflow-hidden rounded-b-xl pointer-events-none">
          <div
            className={cn('h-full', config.accent)}
            style={{
              animation: `toastProgress ${duration}ms linear forwards`,
            }}
          />
        </div>
      )}
    </div>
  );
}
